import { Collapse, CollapsibleIconButton, Stack, Typography } from '@chronosphereio/chrono-ui';
import { type ReactNode, useState } from 'react';

export interface QueryEditorOptionsCollapseProps {
  /** Header text for the collapsible section. */
  title: ReactNode;
  /**
   * Content shown inline with the header when collapsed. Truncated with
   * ellipsis on overflow. Compose `OptionSummaryItem` or arbitrary content.
   */
  summary?: ReactNode;
  /** Expanded content — form fields, selects, etc. Rendered below the header when open. */
  children: ReactNode;
}

/**
 * Collapsible options section rendered outside `Container` as a sibling under
 * `Root`. The component owns collapse state, the header layout (chevron +
 * title + summary), and the expand/collapse toggle.
 *
 * `summary` renders inline with the title when collapsed (hidden when
 * expanded). `children` renders below the header when expanded (hidden when
 * collapsed). Both accept arbitrary ReactNode content.
 */
export function QueryEditorOptionsCollapse({ title, summary, children }: QueryEditorOptionsCollapseProps) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isOpen = !isCollapsed;

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ cursor: 'pointer' }}
        onClick={() => setIsCollapsed((v) => !v)}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <CollapsibleIconButton collapsed={isCollapsed} />
          <Typography variant="body2" fontWeight="bold" color="text.legacySecondary">
            {title}
          </Typography>
        </Stack>
        {isCollapsed && summary && (
          <Typography
            variant="body2"
            color="text.legacySecondary"
            component="div"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
          >
            {summary}
          </Typography>
        )}
      </Stack>
      <Collapse in={isOpen}>{children}</Collapse>
    </Stack>
  );
}
